// src/modules/app-views/goodsReceipt/pages/Document/tabs/RecordTab/NfeIdentificationCard.jsx
// *******************************************************************************
// NFE IDENTIFICATION CARD
// *******************************************************************************
// Card for NFe identification fields: document type (NFe/NFCe), operation type,
// nota fiscal number, serie, operation nature, issue date, entry date,
// purchase order numbers (tags), and access key.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { Row, Col, Form } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleInputArray from 'components/SimpleComponents/SimpleInputArray';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import SimpleSegment from 'components/SimpleComponents/SimpleSegment';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// Document type options (same as invoices)
const DOCUMENT_TYPE_OPTIONS = [
  { value: 'nfe', label: 'NF-e' },
  { value: 'nfce', label: 'NFC-e' },
];

// Operation type options for the segmented control
const OPERATION_TYPE_OPTIONS = [
  { value: 'national', labelKey: 'national' },
  { value: 'import', labelKey: 'imported' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const NfeIdentificationCard = ({ data, viewMode }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const form = Form.useFormInstance();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';

  // Watch documentType to enforce business rules
  const documentType = Form.useWatch('documentType', form);
  const isNfce = documentType === 'nfce';

  // Build segment options with translated labels
  const operationTypeOptions = useMemo(
    () =>
      OPERATION_TYPE_OPTIONS.map(opt => ({
        value: opt.value,
        label: translateX(opt.labelKey),
      })),
    [],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Rule: NFC-e is always a national operation — force operationType when documentType changes
  useEffect(() => {
    if (isNfce) {
      form.setFieldValue('operationType', 'national');
    }
  }, [isNfce, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Group 1: Identification fields (wraps naturally on SM) */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={7} lg={6}>
          <SimpleSegment
            name="documentType"
            label="document_type"
            options={DOCUMENT_TYPE_OPTIONS}
            disabled={isLocked}
          />
        </Col>
        <Col xs={24} sm={7} lg={6}>
          <SimpleSegment
            name="operationType"
            label="operation_type"
            options={operationTypeOptions}
            // Disabled when locked OR when NFC-e (NFC-e is always national)
            disabled={isLocked || isNfce}
          />
        </Col>
        <Col xs={24} sm={6} lg={3}>
          <SimpleNumber
            name="documentNumber"
            label="nota_fiscal"
            disabled={isLocked}
          />
        </Col>
        <Col xs={24} sm={4} lg={2}>
          <SimpleNumber
            name="documentSerie"
            label="serie"
            disabled={isLocked}
          />
        </Col>
        <Col xs={24} sm={10} lg={7}>
          <SimpleInput
            name="operationNature"
            label="operation_nature"
            disabled={isLocked}
          />
        </Col>

        <Col xs={24} sm={7} lg={4}>
          <SimpleDatePicker
            name="issueDate"
            label="issue_date"
            disabled={isLocked}
          />
        </Col>
        <Col xs={24} sm={7} lg={4}>
          <SimpleDatePicker
            name="entryDate"
            label="entry_date"
            disabled={isLocked}
          />
        </Col>
        <Col xs={24} sm={10} lg={7}>
          <SimpleInputArray
            name="purchaseOrderNumber"
            label="orders"
            disabled={isLocked}
          />
        </Col>
        <Col xs={24} sm={14} lg={9}>
          <SimpleInput
            name="accessKey"
            label="access_key"
            disabled={isLocked}
            maxLength={44}
          />
        </Col>
      </Row>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default NfeIdentificationCard;
